import {
  Component,
  Input,
  TemplateRef,
  ViewChild,
  ElementRef,
  OnInit,
  ViewContainerRef,
  OnDestroy,
  EventEmitter,
  Output
} from '@angular/core';
import { Overlay, OverlayRef, OverlayConfig, ConnectedPosition, FlexibleConnectedPositionStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { PopoverService } from '../../../services/pop-over.service';

@Component({
  selector: 'app-popover',
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss']
})
export class PopoverComponent implements OnInit, OnDestroy {
  @ViewChild('origin') origin: ElementRef;
  @ViewChild('contentTemplate') contentTemplate: TemplateRef<any>;

  @Input() placement: 'top' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'right' = 'bottom';
  @Input() offset = 8;
  @Input() closeOnOutsideClick = true;
  @Output() closeDialog = new EventEmitter();

  private outsideMouseDownListener: (event: MouseEvent) => void;
  private overlayRef: OverlayRef;
  // FlexibleConnectedPositionStrategy を保持するための変数
  private positionStrategy: FlexibleConnectedPositionStrategy;
  private positionChangeSubscription: Subscription;
  private keydownSubscription: Subscription;
  isOpen = false;
  // primary および fallback の候補を設定する
  private positions: ConnectedPosition[] = [];

  constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef, private popoverService: PopoverService) {}

  ngOnInit(): void {
    this.setupPositions();
  }

  ngOnDestroy(): void {
    this.unsubscribePositionChange();
  }

  private setupPositions(): void {
    const primaryPosition: ConnectedPosition = this.getPosition(this.placement);
    // fallback 候補として、primary と被らない配置を追加
    const fallbackPositions: ConnectedPosition[] = [
      this.getPosition('bottom'),
      this.getPosition('top'),
      this.getPosition('right'),
      this.getPosition('left')
    ].filter(
      pos =>
        !(
          pos.originX === primaryPosition.originX &&
          pos.originY === primaryPosition.originY &&
          pos.overlayX === primaryPosition.overlayX &&
          pos.overlayY === primaryPosition.overlayY
        )
    );
    // primary を先頭に fallback を追加
    this.positions = [primaryPosition, ...fallbackPositions];
  }

  private getPosition(placement: string): ConnectedPosition {
    switch (placement) {
      case 'top':
        return {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -this.offset
        };
      case 'bottom':
        return {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: this.offset
        };
      case 'left':
        return {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -this.offset
        };
      case 'right':
        return {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: this.offset
        };
      case 'bottom-start':
        return {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: this.offset
        };
      case 'bottom-end':
        return {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
          offsetY: this.offset
        };
      default:
        return {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: this.offset
        };
    }
  }

  onTriggerClick(event: MouseEvent): void {
    event.stopPropagation(); // 外部クリック判定防止
    this.open();
  }

  // toggle(): void {
  //   this.isOpen ? this.close() : this.open();
  // }

  open(): void {
    if (this.isOpen) {
      return;
    }
    this.isOpen = true;

    this.popoverService.register(this);

    // FlexibleConnectedPositionStrategy を生成し、fallback も含める
    this.positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.origin)
      .withPositions(this.positions)
      .withPush(true)
      .withViewportMargin(8)
      .withGrowAfterOpen(true);

    const config = new OverlayConfig({
      positionStrategy: this.positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false // 外部クリック時のクローズ処理は別で実装するため
      // backdropClass: 'cdk-overlay-transparent-backdrop'
    });

    this.overlayRef = this.overlay.create(config);
    const portal = new TemplatePortal(this.contentTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
    // if (this.closeOnOutsideClick) {
    //   this.overlayRef.backdropClick().subscribe(() => this.close());
    // }

    if (this.closeOnOutsideClick) {
      // 外部クリック時のクローズ処理
      this.outsideMouseDownListener = (event: MouseEvent) => {
        const originEl = this.origin?.nativeElement;
        const overlayEl = this.overlayRef?.overlayElement;
        const targetNode = event.target as Node;

        if (originEl && overlayEl && !originEl.contains(targetNode) && !overlayEl.contains(targetNode)) {
          this.close();
        }
      };
      document.addEventListener('mousedown', this.outsideMouseDownListener, true);
    }

    this.keydownSubscription = this.overlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.close();
      }
    });

    // 位置変更のたびに、トリガー(origin)が viewport 内に存在するかを確認し、完全に画面外なら閉じる
    this.positionChangeSubscription = this.positionStrategy.positionChanges.subscribe(() => {
      const originRect: DOMRect = this.origin.nativeElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // origin が上下左右のいずれか完全に画面外にある場合、ポップオーバーを閉じる
      if (originRect.bottom <= 0 || originRect.top >= viewportHeight || originRect.right <= 0 || originRect.left >= viewportWidth) {
        this.close();
      }
    });
  }

  close(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }

    if (this.outsideMouseDownListener) {
      document.removeEventListener('click', this.outsideMouseDownListener, true);
      this.outsideMouseDownListener = null;
    }

    this.unsubscribePositionChange();
    this.popoverService.unregister(this);
    this.isOpen = false;

    this.closeDialog.emit();
  }

  private unsubscribePositionChange(): void {
    if (this.positionChangeSubscription) {
      this.positionChangeSubscription.unsubscribe();
      this.positionChangeSubscription = null;
    }

    if (this.keydownSubscription) {
      this.keydownSubscription.unsubscribe();
      this.keydownSubscription = null;
    }
  }
}
